import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ConnectAlternative } from './connect-alternative';
import { connectAlternativesMessages as messages } from './connect-alternatives-messages';
import { ConnectAlternativeOption } from './ConnectAlternativeOption';

type Props = {
  open: boolean;
  onClose: () => void;
  platform: SocialPlatform;
  alternatives: ConnectAlternative[];
};

/**
 * Compact modal listing clickable connect alternatives with a clear recommended primary path.
 * Once an alternative is picked the dialog locks (no close, options disabled). OAuth swaps
 * the title to a redirecting message; in-app routes keep the choose-method title.
 */
export function ConnectAlternativesDialog({ open, onClose, platform, alternatives }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedAlternative = alternatives.find((alternative) => alternative.id === selectedId);
  const isBusy = selectedId !== null;
  // Only OAuth leaves the app; in-app routes keep the choose-method title.
  const isOauthRedirect = selectedAlternative?.action.type === 'oauth';

  // Clear the selection lock whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setSelectedId(null);
    }
  }, [open]);

  const handleClose = () => {
    if (isBusy) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isBusy}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ pb: 0.5, pr: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {platform.Icon({ colored: true, fontSize: 'small' })}
          {isOauthRedirect ? (
            <FormattedMessage {...messages.redirecting} values={{ platform: platform.name }} />
          ) : (
            <FormattedMessage {...messages.dialogTitle} values={{ platform: platform.name }} />
          )}
        </Stack>
        {!isBusy && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 2 }}>
          {alternatives.map((alternative) => (
            <ConnectAlternativeOption
              key={alternative.id}
              platform={platform}
              alternative={alternative}
              disabled={isBusy}
              redirecting={selectedId === alternative.id}
              onSelect={() => setSelectedId(alternative.id)}
            />
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
